import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  CheckCircle, XCircle, Search, Pause, Ban, Eye, 
  AlertTriangle, History, User, Calendar, MessageSquare,
  RefreshCw, ChevronRight
} from 'lucide-react';

// Mapeo de iconos
const ESTADO_ICONS = {
  'ACTIVO': CheckCircle,
  'DADO_DE_BAJA': XCircle,
  'EN_INVESTIGACION': Search,
  'SUSPENDIDO': Pause,
  'BLOQUEADO': Ban,
  'OBSERVADO': Eye
};

const ClienteEstadoManager = ({ clienteCuit, clienteNombre }) => {
  const [estadoActual, setEstadoActual] = useState(null);
  const [estados, setEstados] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);
  
  // Dialog state
  const [showChangeDialog, setShowChangeDialog] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [comentario, setComentario] = useState('');

  useEffect(() => {
    loadEstadosConfig();
    if (clienteCuit) {
      loadEstadoCliente();
    }
  }, [clienteCuit]);

  const loadEstadosConfig = async () => {
    try {
      const response = await api.get('/clientes/estados/config');
      setEstados(response.data.estados || []);
    } catch (error) {
      console.error('Error loading states config:', error);
    }
  };

  const loadEstadoCliente = async () => {
    try {
      const response = await api.get(`/clientes/${clienteCuit}/estado`);
      setEstadoActual(response.data.estado_actual);
      setHistorial(response.data.historial || []);
    } catch (error) {
      // Si no tiene estado, es ACTIVO por defecto
      setEstadoActual('ACTIVO');
      setHistorial([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEstado = async () => {
    if (!nuevoEstado) {
      toast.error('Selecciona un estado');
      return;
    }
    
    if (nuevoEstado === estadoActual) {
      toast.error('El cliente ya está en este estado');
      return;
    }
    
    // Para estados críticos, requerir comentario
    const estadosCriticos = ['EN_INVESTIGACION', 'BLOQUEADO', 'SUSPENDIDO', 'DADO_DE_BAJA'];
    if (estadosCriticos.includes(nuevoEstado) && !comentario.trim()) {
      toast.error('Debes agregar un comentario para este cambio de estado');
      return;
    }
    
    setChanging(true);
    try {
      await api.post(`/clientes/${clienteCuit}/estado`, {
        nuevo_estado: nuevoEstado,
        comentario: comentario
      });
      
      toast.success(`Estado cambiado a ${getEstadoNombre(nuevoEstado)}`);
      setShowChangeDialog(false);
      setNuevoEstado('');
      setComentario('');
      loadEstadoCliente();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    } finally {
      setChanging(false);
    }
  };

  const getEstadoInfo = (estadoId) => {
    return estados.find(e => e.id === estadoId) || {
      nombre: estadoId,
      color: '#6B7280',
      icon: 'circle'
    };
  };

  const getEstadoNombre = (estadoId) => {
    return getEstadoInfo(estadoId).nombre;
  };

  const getEstadoIcon = (estadoId) => {
    return ESTADO_ICONS[estadoId] || CheckCircle;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card className="p-6 bg-white">
        <div className="flex items-center justify-center">
          <RefreshCw className="animate-spin text-slate-400" size={24} />
        </div>
      </Card>
    );
  }

  const estadoInfo = getEstadoInfo(estadoActual);
  const EstadoIcon = getEstadoIcon(estadoActual);

  return (
    <div className="space-y-4">
      {/* Estado Actual */}
      <Card className="p-6 bg-white">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div 
              className="p-3 rounded-xl"
              style={{ backgroundColor: `${estadoInfo.color}20` }}
            >
              <EstadoIcon size={28} style={{ color: estadoInfo.color }} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Estado del Cliente</p>
              <h3 className="text-xl font-bold" style={{ color: estadoInfo.color }}>
                {estadoInfo.nombre}
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                {estadoInfo.descripcion}
              </p>
            </div>
          </div>
          
          <Button onClick={() => setShowChangeDialog(true)}>
            Cambiar Estado
          </Button>
        </div>
        
        {/* Alerta para estados críticos */}
        {['EN_INVESTIGACION', 'BLOQUEADO', 'SUSPENDIDO'].includes(estadoActual) && (
          <div 
            className="mt-4 p-4 rounded-lg flex items-start gap-3"
            style={{ backgroundColor: `${estadoInfo.color}10`, borderLeft: `4px solid ${estadoInfo.color}` }}
          >
            <AlertTriangle size={20} style={{ color: estadoInfo.color }} />
            <div>
              <p className="font-medium" style={{ color: estadoInfo.color }}>
                Cliente en estado {estadoInfo.nombre}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                {estadoActual === 'EN_INVESTIGACION' && 'Este cliente está siendo investigado. Considere revisar sus movimientos y alertas.'}
                {estadoActual === 'BLOQUEADO' && 'Este cliente ha sido bloqueado. No puede realizar operaciones.'}
                {estadoActual === 'SUSPENDIDO' && 'La operatoria del cliente está temporalmente suspendida.'}
              </p>
            </div>
          </div>
        )}
      </Card>
      
      {/* Historial de Estados */}
      <Card className="p-6 bg-white">
        <div className="flex items-center gap-2 mb-4">
          <History size={20} className="text-slate-400" />
          <h4 className="font-semibold text-slate-900">Historial de Estados</h4>
        </div>
        
        {historial.length === 0 ? (
          <p className="text-slate-500 text-sm">No hay cambios de estado registrados</p>
        ) : (
          <div className="space-y-3">
            {historial.slice().reverse().map((entry, idx) => {
              const estadoAnteriorInfo = getEstadoInfo(entry.estado_anterior);
              const estadoNuevoInfo = getEstadoInfo(entry.estado_nuevo);
              
              return (
                <div 
                  key={entry.id || idx}
                  className="flex items-start gap-4 p-3 rounded-lg bg-slate-50 border border-slate-100"
                >
                  <div className="flex items-center gap-2 min-w-[200px]">
                    <Badge 
                      variant="outline" 
                      style={{ borderColor: estadoAnteriorInfo.color, color: estadoAnteriorInfo.color }}
                    >
                      {estadoAnteriorInfo.nombre}
                    </Badge>
                    <ChevronRight size={16} className="text-slate-400" />
                    <Badge 
                      style={{ backgroundColor: estadoNuevoInfo.color, color: 'white' }}
                    >
                      {estadoNuevoInfo.nombre}
                    </Badge>
                  </div>
                  
                  <div className="flex-1">
                    {entry.comentario && (
                      <div className="flex items-start gap-2 mb-2">
                        <MessageSquare size={14} className="text-slate-400 mt-0.5" />
                        <p className="text-sm text-slate-600">{entry.comentario}</p>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <User size={12} />
                        {entry.changed_by_email || 'Sistema'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(entry.changed_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
      
      {/* Dialog: Cambiar Estado */}
      <Dialog open={showChangeDialog} onOpenChange={setShowChangeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cambiar Estado del Cliente</DialogTitle>
            <DialogDescription>
              {clienteNombre || clienteCuit}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Estado Actual</Label>
              <div className="flex items-center gap-2 mt-1">
                <Badge style={{ backgroundColor: estadoInfo.color, color: 'white' }}>
                  {estadoInfo.nombre}
                </Badge>
              </div>
            </div>
            
            <div>
              <Label>Nuevo Estado *</Label>
              <Select value={nuevoEstado} onValueChange={setNuevoEstado}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecciona el nuevo estado" />
                </SelectTrigger>
                <SelectContent>
                  {estados.filter(e => e.id !== estadoActual).map(estado => {
                    const Icon = getEstadoIcon(estado.id);
                    return (
                      <SelectItem key={estado.id} value={estado.id}>
                        <div className="flex items-center gap-2">
                          <Icon size={16} style={{ color: estado.color }} />
                          {estado.nombre}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>
                Comentario / Motivo
                {['EN_INVESTIGACION', 'BLOQUEADO', 'SUSPENDIDO', 'DADO_DE_BAJA'].includes(nuevoEstado) && (
                  <span className="text-red-500"> *</span>
                )}
              </Label>
              <Textarea
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="Describe el motivo del cambio de estado..."
                rows={3}
                className="mt-1"
              />
              {['EN_INVESTIGACION', 'BLOQUEADO', 'SUSPENDIDO', 'DADO_DE_BAJA'].includes(nuevoEstado) && (
                <p className="text-xs text-amber-600 mt-1">
                  El comentario es obligatorio para este tipo de cambio de estado
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangeDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleChangeEstado} 
              disabled={changing}
              style={nuevoEstado ? { 
                backgroundColor: getEstadoInfo(nuevoEstado).color 
              } : {}}
            >
              {changing ? (
                <>
                  <RefreshCw className="animate-spin mr-2" size={16} />
                  Cambiando...
                </>
              ) : (
                'Confirmar Cambio'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClienteEstadoManager;
